import { useState } from "react";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { LayoutTemplate, Trash2 } from "lucide-react";
import type { ShipperDispatchTemplate } from "@/hooks/useShipperDispatchTemplates";
import { toast } from "sonner";

interface Props {
  templates: ShipperDispatchTemplate[];
  loading: boolean;
  hasUnsavedContent: boolean;
  onApply: (tpl: ShipperDispatchTemplate) => Promise<void> | void;
  onDelete: (id: string) => Promise<void> | void;
}

export function TemplatePickerDropdown({ templates, loading, hasUnsavedContent, onApply, onDelete }: Props) {
  const [pending, setPending] = useState<ShipperDispatchTemplate | null>(null);

  const handlePick = (tpl: ShipperDispatchTemplate) => {
    if (hasUnsavedContent) {
      setPending(tpl);
    } else {
      void onApply(tpl);
    }
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="sm" className="gap-1.5">
            <LayoutTemplate className="h-4 w-4" /> Use Template
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-72">
          <DropdownMenuLabel>Your dispatch templates</DropdownMenuLabel>
          <DropdownMenuSeparator />
          {loading && <DropdownMenuItem disabled>Loading…</DropdownMenuItem>}
          {!loading && templates.length === 0 && (
            <DropdownMenuItem disabled className="text-xs text-muted-foreground">
              No templates yet — use "Save as Template" to create one.
            </DropdownMenuItem>
          )}
          {!loading && templates.map((t) => (
            <DropdownMenuItem
              key={t.id}
              onSelect={(e) => { e.preventDefault(); handlePick(t); }}
              className="flex items-start gap-2 py-2"
            >
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium truncate">{t.name}</div>
                <div className="text-[11px] text-muted-foreground truncate">
                  {t.last_used_at ? `Last used ${new Date(t.last_used_at).toLocaleDateString()}` : "Never used"} · {t.use_count} use{t.use_count === 1 ? "" : "s"}
                </div>
              </div>
              <button
                type="button"
                className="text-muted-foreground hover:text-destructive p-1"
                onClick={async (e) => {
                  e.stopPropagation();
                  if (!confirm(`Delete template "${t.name}"?`)) return;
                  try { await onDelete(t.id); toast.success("Template deleted"); }
                  catch (err: any) { toast.error(err?.message || "Failed to delete"); }
                }}
                title="Delete template"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>

      <AlertDialog open={!!pending} onOpenChange={(v) => { if (!v) setPending(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Overwrite current dispatch fields?</AlertDialogTitle>
            <AlertDialogDescription>
              Applying "{pending?.name}" will replace the stops, pallets, weight, commodity and notes you've already entered. Pickup and delivery dates won't change.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-brand-teal hover:bg-brand-teal/90"
              onClick={async () => {
                const t = pending;
                setPending(null);
                if (t) await onApply(t);
              }}
            >
              Apply template
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
